import { DatasourceCanBaseIcon } from '@tableau/icons/DatasourceCanBaseIcon';
import { DatasourceCanEmbeddedIcon } from '@tableau/icons/DatasourceCanEmbeddedIcon';
import { DatasourceCanExtractEmbeddedIcon } from '@tableau/icons/DatasourceCanExtractEmbeddedIcon';
import { DatasourceCanExtractIcon } from '@tableau/icons/DatasourceCanExtractIcon';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Filter } from '~commonInfra/utils/Filter';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { Set } from '~commonInfra/utils/Set';
import { updateOrDeleteCertificationForItem } from '~src/catalog/restApi/dataQuality/setDataQualityCertificationForItem';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { RemoteConnectionDetailInfo } from '~src/datasource/dataConnection/models/ConnectionDetailInfo';
import { DataConnections, IConnectionUpdate } from '~src/datasource/dataConnection/resources/DataConnections';
import {
  GetExtractRefreshSchedulingForDatasourceRequest,
  IGetExtractRefreshSchedulingForDatasourceResult
} from '~src/datasource/ServerApi/requests/GetExtractRefreshSchedulingForDatasourceRequest';
import {
  ISetDatasourceAskDataSettingsParams,
  SetDatasourceAskDataSettingsRequest
} from '~src/datasource/ServerApi/requests/SetDatasourceAskDataSettingsRequest';
import { WebClientDetailFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { ITaggableContent } from '~src/tags/ITaggableContent';

import { Messages } from '../../codegen/Localize';
import { ExtractEncryptionMode } from '../../commonInfra/utils/sites/SitePropertyTypes';
import {
  GetPreferredRefreshOptionByUrlNameRequest,
  GetPreferredRefreshOptionRequest,
  IGetPreferredRefreshOptionByUrlNameResult,
  IGetPreferredRefreshOptionResult
} from '../../datasource/ServerApi/requests/GetPreferredRefreshOption';
import { CancelExtractCreationRequest } from '../../extractTask/serverApi/requests/CancelExtractCreationRequest';
import { CreateExtractTasksRequest } from '../../extractTask/serverApi/requests/CreateExtractTasksRequest';
import { ExtractTaskTargetType } from '../../extractTask/serverApi/types/IExtractTask';
import { IDetailFetcher } from '../../httpClient/fetchers/IDetailFetcher';
import { IListFetcher } from '../../httpClient/fetchers/IListFetcher';
import { ListResult } from '../../httpClient/fetchers/ListResult';
import { FieldValuesQuery } from '../../httpClient/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../httpClient/resources/Resource';
import { ResourceQuery } from '../../httpClient/resources/ResourceQuery';
import { GetFieldValuesRequest } from '../../httpClient/serverApi/requests/GetFieldValuesRequest';
import { IFieldValue } from '../../httpClient/serverApi/types/IFieldValue';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { SliceableHelper } from '../../httpClient/utils/SliceableHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { DatasourceContentType } from '../../spacesPlatform/models/ContentTypes';
import { FilterFieldNames } from '../../spacesPlatform/models/IFilterClause';
import { ICertifiableItemInfo, IExplorableItemTypeInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { IDataConnectionInfo } from '../dataConnection/models/IDataConnectionInfo';
import { ICheckConnectionResult } from '../dataConnection/serverApi/CheckConnectionRequest';
import { ExtractEncryptionState, IDataSourceDetailInfo, IDataSourceInfo } from '../models/IDataSourceInfo';
import { CommonDatasourceInfo } from '../models/mappers/CommonDatasourceInfo';
import { DatasourceMapper } from '../models/mappers/DatasourceMapper';
import { AddTagsToDatasourcesRequest } from '../ServerApi/requests/AddTagsToDatasourcesRequest';
import { ChangeExtractToLiveActionOnDatasourcesRequest } from '../ServerApi/requests/ChangeExtractToLiveActionOnDatasourcesRequest';
import { DeleteDatasourcesRequest } from '../ServerApi/requests/DeleteDatasourcesRequest';
import { GetDatasourceActionsRequest } from '../ServerApi/requests/GetDatasourceActionsRequest';
import { GetDatasourcesRequest } from '../ServerApi/requests/GetDatasourcesRequest';
import { MoveDatasourcesToProjectRequest } from '../ServerApi/requests/MoveDatasourcesToProjectRequest';
import { PerformExtractEncryptionActionOnDatasourcesRequest } from '../ServerApi/requests/PerformExtractEncryptionActionOnDatasourcesRequest';
import { RemoveTagsFromDatasourcesRequest } from '../ServerApi/requests/RemoveTagsFromDatasourcesRequest';
import { RenameDatasourceRequest } from '../ServerApi/requests/RenameDatasourceRequest';
import { RunCreateExtractsOnDatasourcesRequest } from '../ServerApi/requests/RunCreateExtractsOnDatasourcesRequest';
import { RunExtractRefreshesOnDatasourcesRequest } from '../ServerApi/requests/RunExtractRefreshesOnDatasourcesRequest';
import { ISetDatasourceCertificationParams } from '../ServerApi/requests/SetDatasourceCertificationRequest';
import { SetDatasourceDescriptionRequest } from '../ServerApi/requests/SetDatasourceDescriptionRequest';
import {
  ISetDatasourceRefreshModeParams,
  SetDatasourceRefreshModeRequest
} from '../ServerApi/requests/SetDatasourceRefreshModeRequest';
import {
  ISetDatasourceRemoteAgentParams,
  SetDatasourceRemoteAgentRequest
} from '../ServerApi/requests/SetDatasourceRemoteAgentRequest';
import {
  ISetDatasourceRemoteAgentReservationParams,
  ISetDatasourceRemoteAgentReservationResult,
  SetDatasourceRemoteAgentReservationRequest
} from '../ServerApi/requests/SetDatasourceRemoteAgentReservationRequest';
import {
  ISetDatasourceRemoteQueryAgentParams,
  SetDatasourceRemoteQueryAgentRequest
} from '../ServerApi/requests/SetDatasourceRemoteQueryAgentRequest';
import { SetDatasourcesOwnerRequest } from '../ServerApi/requests/SetDatasourcesOwnerRequest';
import {
  ISetDatasourceUsingRemoteQueryAgentParams,
  SetDatasourceUsingRemoteQueryAgentRequest
} from '../ServerApi/requests/SetDatasourceUsingRemoteQueryAgentRequest';
import { IDatasourceActions } from '../ServerApi/types/IDatasourceActions';


// Map of data source icons for each size.
// This keeps the image file paths intact and keeps logic simple
// 'medium' is used for list view and the workbook card tooltip
export type DatasourceIconSVGSize = 'tooltip' | 'masthead' | 'card';
type DatasourceImagesPerSize = Record<DatasourceIconSVGSize, string>;
type DatasourceIconType = 'embedded' | 'extract' | 'extract_embedded' | 'live';
const DatasourceSVGs: Record<DatasourceIconType, DatasourceImagesPerSize> = {
  extract_embedded: {
    'tooltip': 'datasource_extract_embedded_18px-large.svg',
    'masthead': 'datasource_extract embedded_80px_empty.svg',
    'card': 'datasource_extract_embedded_60px-card.svg',
  },
  embedded: {
    'tooltip': 'datasource_embedded_18px-large.svg',
    'masthead': 'spaces_masthead_ds_embedded.svg',
    'card': 'spaces_card_ds_embedded.svg',
  },
  extract: {
    'tooltip': 'datasource_extract_18px-large.svg',
    'masthead': 'spaces_masthead_extract.svg',
    'card': 'spaces_card_extract.svg',
  },
  live: {
    'tooltip': 'datasource_18px-large.svg',
    'masthead': 'spaces_card_ds.svg',  // this doesn't look good left-aligned in masthead so use the centered one
    'card': 'spaces_card_ds.svg',
  },
};

export type DatasourceIconSize = 'list';
type DatasourceIconsPerSize = Record<DatasourceIconSize, (props: any) => React.ReactSVGElement | null>;
const DatasourceIcons: Record<DatasourceIconType, DatasourceIconsPerSize> = {
  extract_embedded: {
    'list': DatasourceCanExtractEmbeddedIcon,
  },
  embedded: {
    'list': DatasourceCanEmbeddedIcon,
  },
  extract: {
    'list': DatasourceCanExtractIcon,
  },
  live: {
    'list': DatasourceCanBaseIcon,
  },
};

export function getDataConnectionInfos(dataConnectionsOrSources: IContentInfo[], queryField: string): Promise<IDataConnectionInfo[]> {
  const connectionOrSourceIdArray = dataConnectionsOrSources.map(w => w.id);

  const makeQueryFromIdsBlock = (idsBlock: string[]) =>
    DataConnections.all().addFilter(Filter.stringFieldInArray(queryField, idsBlock));

  return SliceableHelper.fetchAllForSliceableWithArrayParam(
    connectionOrSourceIdArray,
    makeQueryFromIdsBlock
  );
}

export class DataSourcesResource implements IListResource<IDataSourceInfo>, IFieldValuesResource, IDetailResource<IDataSourceDetailInfo>, IActionResource<IDatasourceActions>, ITaggableContent {

  public isPublished(datasource: CommonDatasourceInfo): boolean {
    return !datasource.workbook;
  }

  public isEmbedded(datasource: CommonDatasourceInfo): boolean {
    return !this.isPublished(datasource);
  }

  public isExtract(datasource: CommonDatasourceInfo): boolean {
    return datasource.hasExtracts;
  }

  public isUnencrypted(datasource: CommonDatasourceInfo): boolean {
    return datasource.extractEncryptionState === ExtractEncryptionState.unencrypted;
  }
  public isEncrypted(datasource: CommonDatasourceInfo): boolean {
    return datasource.extractEncryptionState === ExtractEncryptionState.encrypted;
  }

  public isPendingCreateExtract(datasource: CommonDatasourceInfo): boolean {
    return datasource.extractCreationPending;
  }

  public isPendingEncryption(datasource: CommonDatasourceInfo): boolean {
    return datasource.extractEncryptionState === ExtractEncryptionState.pendingEncryption;
  }

  public isPendingDecryption(datasource: CommonDatasourceInfo): boolean {
    return datasource.extractEncryptionState === ExtractEncryptionState.pendingDecryption;
  }

  public getDatasourceInfoFromDataConnection(dataconnection: IDataConnectionInfo): IDataSourceInfo {
    // We want to treat the datasource property of a data connection object retrieved from GetDataConnections as a UnifiedDatasource
    // The id is a unified Id data source and we used the certificationDetails property to test for this. See comment in isUnifiedDatasource
    return { ...dataconnection.datasource, contentType: 'datasource', certificationDetails: {} as ICertifiableItemInfo } as IDataSourceInfo;
  }

  public isUnifiedDatasource(contentItem: IExplorableItemTypeInfo): contentItem is IDataSourceInfo {
    // There are two SOLR Cores that have data sources: the unified data source core and the content core
    // The content core has different ids for data sources than the unified data source core.
    // The data source core stores certificationDetails as a separate property for each data source and
    // the data source maps it back to the UI code. The content core maps the certification details into
    // the property 'contentDetail.' So, here we can check if the item has 'certificationDetails' on it
    // know if it's from the unified data source core.
    // getDatasource and getDatasources are from the unified data source core; exploreContent is from
    // the content core.
    return contentItem.contentType
      && contentItem.contentType === 'datasource'
      && contentItem.hasOwnProperty('certificationDetails');
  }

  public isCertified(contentItem: CommonDatasourceInfo): boolean {
    return FeatureFlags.datasourceCertificationEnabled
      && contentItem.certificationDetails && contentItem.certificationDetails.isCertified;
  }

  public hasDataQualityWarning(contentItem: CommonDatasourceInfo): boolean {
    return contentItem.dataQualityIndicators
      && contentItem.dataQualityIndicators[0]
      && contentItem.dataQualityIndicators[0].isActive;
  }

  public isUploadFile(datasource: CommonDatasourceInfo): boolean {
    return datasource.isFileBased;
  }

  public isConnectionToPublishedDS(datasource: CommonDatasourceInfo): boolean {
    return datasource.publishedDatasourceDetails ? true : false;
  }

  public getIconSVGForDatasourceType(datasourceItem: CommonDatasourceInfo, size: DatasourceIconSVGSize): string {

    // there are 3 cases need show extract_embedded icon
    // 1) ebmedded ds with extract to file
    // 2) ebmedded ds with extract to published ds with live/extract
    if (DataSources.isEmbedded(datasourceItem) && DataSources.isExtract(datasourceItem)) {
      return imgPath(DatasourceSVGs['extract_embedded'][size]);
    }

    if (DataSources.isEmbedded(datasourceItem)) {
      return imgPath(DatasourceSVGs['embedded'][size]);
    }

    return this.getIconPathforPublished(this.isExtract(datasourceItem), size);
  }

  public getIconPathforPublished(hasExtracts: boolean, size: DatasourceIconSVGSize): string {
    if (hasExtracts) {
      return imgPath(DatasourceSVGs['extract'][size]);
    } else { // Live Datasource
      return imgPath(DatasourceSVGs['live'][size]);
    }
  }

  public getIconForDatasourceType(datasourceItem: CommonDatasourceInfo, size: DatasourceIconSize): (props: any) => React.ReactSVGElement | null {

    // there are 3 cases need show extract_embedded icon
    // 1) ebmedded ds with extract to file
    // 2) ebmedded ds with extract to published ds with live/extract
    if (DataSources.isEmbedded(datasourceItem) && DataSources.isExtract(datasourceItem)) {
      return DatasourceIcons['extract_embedded'][size];
    }

    if (DataSources.isEmbedded(datasourceItem)) {
      return DatasourceIcons['embedded'][size];
    }

    return this.getIconforPublished(this.isExtract(datasourceItem), size);
  }

  public getIconforPublished(hasExtracts: boolean, size: DatasourceIconSize): (props: any) => React.ReactSVGElement | null {
    if (hasExtracts) {
      return DatasourceIcons['extract'][size];
    } else { // Live Datasource
      return DatasourceIcons['live'][size];
    }
  }

  public DatasourceStatus(isExtractEncryptionEnabled: boolean, connectionType: string, isFileBased: boolean, hasExtracts: boolean,
    extractEncryptionState: ExtractEncryptionState, extractCreationPending: boolean, siteEaRMode: ExtractEncryptionMode, lastRefreshedCell?: boolean): string {
    const messages = new Map<ExtractEncryptionState, string>([
      [ExtractEncryptionState.encrypted, Messages.ear_Extract_Encryption_State_Encrypted()],
      [ExtractEncryptionState.pendingDecryption, Messages.ear_Extract_Encryption_State_Decrypting_mttw()],
      [ExtractEncryptionState.pendingEncryption, Messages.ear_Extract_Encryption_State_Encrypting_mttw()],
      [ExtractEncryptionState.unencrypted, siteEaRMode === ExtractEncryptionMode.DISABLED ? Messages.extract() : Messages.ear_Extract_Encryption_State_Unencrypted()]
    ]);

    if (extractCreationPending) {
      return Messages.ExtractCreationPending();
    }

    if (!lastRefreshedCell) {
      if (isExtractEncryptionEnabled && hasExtracts) {
        const result = messages.get(extractEncryptionState);
        if (!_.isUndefined(result)) {
          return result;
        }
      }
    }

    if (isFileBased && connectionType !== 'sqlproxy' && !hasExtracts) {
      const result = Messages.fileBased();
      return result;
    }

    return hasExtracts ? Messages.extract() : Messages.liveConnection();
  }

  private listFetcher: IListFetcher<IDataSourceInfo>;
  private allQuery: ResourceQuery<IDataSourceInfo>;
  private detailFetcher: IDetailFetcher<IDataSourceDetailInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor() {
    this.listFetcher = new WebClientListResourceFetcher(GetDatasourcesRequest, ServerApiMapper.dataSources);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.detailFetcher = new WebClientDetailFetcher('getDatasource', DatasourceMapper.mapDetail);
    this.fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public all(): ResourceQuery<IDataSourceInfo> {
    return this.allQuery;
  }

  public published(): ResourceQuery<IDataSourceInfo> {
    return this.all().addFilter(Filter.fieldEq(FilterFieldNames.isPublished, true));
  }

  public getByWorkbookId(id: string): Promise<ListResult<IDataSourceInfo>> {
    const query = this.all().addFilter(Filter.fieldEq(FilterFieldNames.workbookId, id));
    return SliceableHelper.fetchAll(query);
  }

  public getById(id: string): Promise<IDataSourceDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  /**
   * Takes one or more IContentInfo objects and returns either:
   *  1) the corresponding IDataSourceInfo objects, fetched from the API.
   *  2) the same objects, if they're already IDataSourceInfo objects.
   *
   * If IDataSourceInfo objects are fetched from the API, datasource IDs are first mapped to
   * unified datasource IDs (eg: connection IDs).
   *
   * The function only evaulates the first content info item to decide which approach is needed.
   *
   * @param content One or more IContentInfo objects.
   */
  public fromContent(...content: IExplorableItemTypeInfo[]): Promise<IDataSourceInfo[]> {
    const initialIdArray = content.map(d => d.id);

    if (content && content.length > 0) {
      if (this.isUnifiedDatasource(content[0])) {
        // If the first one is already a unified data source, assume they all are.
        return Promise.resolve(content as IDataSourceInfo[]) as Promise<IDataSourceInfo[]>;
      } else {
        // The getDatasource endpoint still expects unified datasource IDs, which are really connection IDs.
        const datasourcesResource = this;
        return DataConnections.getConnectionIdsFromDatasourceIds(initialIdArray).then(map => {
          const unifiedDatasourceIds = _.values(map).map(v => v[0]);
          return datasourcesResource.all()
            .addFilter(Filter.stringFieldInArray('id', unifiedDatasourceIds))
            .getSlice(0, unifiedDatasourceIds.length)
            .then(result => result.items);
        });
      }
    } else {
      return Promise.resolve([]) as Promise<IDataSourceInfo[]>;
    }
  }

  public getPublishedByUrlNames(urlNames: string[]): Promise<ListResult<IDataSourceInfo>> {
    const query = this.all()
      .addFilter(Filter.stringFieldInArray(FilterFieldNames.urlName, urlNames))
      .addFilter(Filter.fieldEq(FilterFieldNames.isPublished, true));

    return SliceableHelper.fetchAll(query);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'datasource');
  }

  // Actions

  public actions(id: string): Promise<IDatasourceActions> {
    return WebHttpClient.sendRequest(new GetDatasourceActionsRequest(id));
  }

  public del(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteDatasourcesRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setCertification(params: ISetDatasourceCertificationParams & { luid?: string }): Promise<IResult> {
    return updateOrDeleteCertificationForItem({ ...params, contentType: 'datasource' });
  }

  public setDescription(id: string, description: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetDatasourceDescriptionRequest(id, description));
  }

  public setAskDataSettings(params: ISetDatasourceAskDataSettingsParams): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetDatasourceAskDataSettingsRequest(params));
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetDatasourcesOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public setRefreshMode(id: string, refreshMode: string): Promise<IResult> {
    const params: ISetDatasourceRefreshModeParams = {
      id: id,
      refreshMode: refreshMode
    };

    return WebHttpClientWithErrorToastInstance.sendRequest(new SetDatasourceRefreshModeRequest(params));
  }

  public setRemoteAgent(id: string, remoteAgentName: string, workflowId?: string): Promise<IResult> {
    const params: ISetDatasourceRemoteAgentParams = {
      id: id,
      remoteAgentName: remoteAgentName,
      workflowId: workflowId
    };

    return WebHttpClientWithErrorToastInstance.sendRequest(new SetDatasourceRemoteAgentRequest(params));
  }

  public setRemoteAgentReservation(id: string, remoteAgentReservationId: string): Promise<ISetDatasourceRemoteAgentReservationResult> {
    const params: ISetDatasourceRemoteAgentReservationParams = {
      id: id,
      remoteAgentReservationId: remoteAgentReservationId
    };

    return WebHttpClientWithErrorToastInstance.sendRequest(new SetDatasourceRemoteAgentReservationRequest(params));
  }

  public moveToProject(targetProjectId: string, datasourceIds: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(datasourceIds, (idsBlock) => new MoveDatasourcesToProjectRequest({
        ids: idsBlock,
        projectId: targetProjectId
      })),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToDatasourcesRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromDatasourcesRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public createExtractTask(ids: string[], scheduleId: string, extractType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new CreateExtractTasksRequest({
        targetType: ExtractTaskTargetType.Datasource,
        targetIds: idsBlock,
        scheduleId: scheduleId,
        type: extractType // ExtractTaskType
      })),
      ResultHelper.combineResults);
  }

  public refreshExtracts(ids: string[], extractType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new RunExtractRefreshesOnDatasourcesRequest(idsBlock, extractType)
      ),
      ResultHelper.combineResults);
  }

  public createExtracts(ids: string[], encrypt: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new RunCreateExtractsOnDatasourcesRequest(idsBlock, encrypt)
      ),
      ResultHelper.combineResults);
  }

  public encryptDataSources(ids: string[], actionType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new PerformExtractEncryptionActionOnDatasourcesRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults);
  }

  public getPreferredRefreshOption(id: string): Promise<IGetPreferredRefreshOptionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetPreferredRefreshOptionRequest({ id: id }));
  }

  public getPreferredRefreshOptionsByUrlName(urlNames: string[]): Promise<IGetPreferredRefreshOptionByUrlNameResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetPreferredRefreshOptionByUrlNameRequest({ urlNames: urlNames }));
  }

  public getExtractRefreshSchedulingForDatasourceRequest(id: string): Promise<IGetExtractRefreshSchedulingForDatasourceResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetExtractRefreshSchedulingForDatasourceRequest({ id: id }));
  }

  public setRemoteQueryAgent(id: string, remoteAgentName: string, useDefaultAgent: boolean): Promise<IResult> {
    const params: ISetDatasourceRemoteQueryAgentParams = {
      id: id,
      remoteAgentName: remoteAgentName,
      useDefaultAgent: useDefaultAgent
    };

    return WebHttpClientWithErrorToastInstance.sendRequest(new SetDatasourceRemoteQueryAgentRequest(params));
  }

  public setUsingRemoteQueryAgent(id: string): Promise<IResult> {
    const params: ISetDatasourceUsingRemoteQueryAgentParams = {
      id: id
    };

    return WebHttpClientWithErrorToastInstance.sendRequest(new SetDatasourceUsingRemoteQueryAgentRequest(params));
  }

  public changeToLive(dataSourceIds: string[], workbookId: string): Promise<IResult> {
    let result = WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(dataSourceIds, (idsBlock) =>
        new ChangeExtractToLiveActionOnDatasourcesRequest(idsBlock, workbookId)
      ),
      ResultHelper.combineResults);
    // Add 5 sec delay on returning result,
    // as default doesn't work well with solr index need more time
    return new Promise<IResult>(resolve =>
      setTimeout(() => resolve(result), DELAY_CHANGE_TO_LIVE_SERVER_RESPONSE)
    );
  }

  public cancelExtractCreationOnDatasources(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new CancelExtractCreationRequest(ids, DatasourceContentType.name)
      ),
      ResultHelper.combineResults);
  }

  public async checkConnections(dataSources: Set<IDataSourceInfo>): Promise<ICheckConnectionResult[]> {
    const dataSourceIdArray: IDataSourceInfo[] = dataSources.toArray();
    const connections: IDataConnectionInfo[] = await getDataConnectionInfos(dataSourceIdArray, 'datasourceId');
    const result: ICheckConnectionResult[] = [];

    for (const connection of connections) {
      const connectionDetail: RemoteConnectionDetailInfo =
        <RemoteConnectionDetailInfo>connection.connectionDetails;
      const connectionInfo: IConnectionUpdate = {
        server: connectionDetail.serverName,
        port: connectionDetail.serverPort,
        username: connectionDetail.databaseUsername,
        passwordUpdate: {
          isEmbedded: connectionDetail.hasEmbeddedPassword,
          newPassword: null
        }
      };

      const response: ICheckConnectionResult = await DataConnections.checkConnection(connection.id, connectionInfo);
      result.push(response);
    }
    return result;
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new RenameDatasourceRequest({ id, name }));
  }
}

export enum RefreshOptionType {
  FeatureDisabled,
  NotSupported,
  LiveConnection,
  Server,
  OnlineSync
}

const DELAY_CHANGE_TO_LIVE_SERVER_RESPONSE = 5000;

export const DataSources = new DataSourcesResource();
